import React, { useEffect, useState } from 'react';
import {
  Spin,
  Descriptions,
  Typography,
  Table,
  Space,
  Popover,
  message,
  Button,
  Input,
  Form,
  DatePicker,
  Switch,
} from 'antd';
import get from 'lodash/get';
import map from 'lodash/map';
import moment from 'moment';
import { useHistory, useParams } from 'react-router-dom';
import {
  RightOutlined,
  InfoCircleOutlined,
  EditOutlined,
  SaveOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import ContentPageHeader from '../../components/contentPageHeader';
import { campaignManagement } from '../../redux/actions/campaignAction';
import {
  sampleCampaigndetails,
  retailCampaigndetails,
  campaignDetailsTableFiltersList,
} from '../../constants/campaign';
import { getTouchPointMeta } from '../../utilities/campaign';
import { CAMPAIGN } from '../../constants/routes';
import { generatePaginationObj } from '../../utilities/helpers';
import {
  UPDATE_CAMPAIGN_SUCCESS,
  DOWNLOAD_CAMPAIGN_CSV_SUCCESS,
} from '../../constants/messages';
import TableFilters from '../../components/tableFilters';
import ShowPaginationLimits from '../../components/showPaginationLimits';

const { Text, Link } = Typography;
const { RangePicker } = DatePicker;

const Content = ({ array = [] }) => {
  return (
    <div>
      {map(array, (arr) => (
        <p>{get(arr, 'name')}</p>
      ))}
    </div>
  );
};

const CampaignDetailScreen = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const [editMode, setEditMode] = useState(false);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [form] = Form.useForm();
  const { id, companyId } = useParams();

  const role = useSelector((state) => get(state, 'auth.role'));
  const [locationsList, setLocationsList] = useState([]);
  const [touchpointsList, setTouchpointsList] = useState([]);
  const [orders, setOrders] = useState({
    data: [],
    filters: null,
    sorters: null,
    pagination: {
      offset: 0,
      limit: 10,
      total: 0,
      current: 1,
    },
    filterChips: null,
  });
  const isSample = get(campaignDetails, 'type') === 'SAMPLE';
  if (!id || !companyId) {
    history.push(CAMPAIGN);
    message.error('Invalid Url');
  }

  const [tempFilter, setTempFilter] = useState({
    filterDropdown: {
      dateRange: false,
      gender: false,
      status: false,
      location: false,
      touchpoints: false,
    },
    tempFilterChips: {
      created_at: undefined,
      gender: undefined,
      status: undefined,
      touchpoint_city_id: undefined,
      touchpoint_id: undefined,
    },
    filterValues: {
      created_at: undefined,
      gender: undefined,
      status: undefined,
      touchpoint_city_id: undefined,
      touchpoint_id: undefined,
    },
  });

  const { offset, limit } = generatePaginationObj(
    get(orders, 'pagination.current'),
    get(orders, 'pagination.limit')
  );

  const fetchLocationsList = () => {
    setLoading(true);
    dispatch(campaignManagement('list.touchpoint_cities'))
      .then((result) => {
        setLoading(false);
        setLocationsList(get(result, 'data'));
      })
      .catch(() => {
        setLoading(false);
      });
  };
  const fetchTouchpointsList = (touchpointCityIds) => {
    setLoading(true);
    dispatch(
      campaignManagement('list.touchpoints_by_cities', {
        touchpoint_city_ids: touchpointCityIds,
      })
    )
      .then((result) => {
        setLoading(false);
        const touchpoint = map(get(result, 'data'), (touchpoints) =>
          get(touchpoints, 'Touchpoints')
        );

        const touchpointList = touchpoint.reduce(
          (accumulator, currentValue) => {
            return accumulator.concat(currentValue);
          },
          []
        );
        setTouchpointsList(touchpointList);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchCampaignDetails = () => {
    setLoading(true);
    dispatch(
      campaignManagement('get.campaign', {
        id: parseInt(id),
        company_id: parseInt(companyId),
      })
    )
      .then((result) => {
        setCampaignDetails(get(result, 'data'));
        setLoading(false);
        setEditMode(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const fetchCampaignOrders = (
    page = 1,
    filterObj = {},
    sorterObj = {},
    filterChipsObj = {},
    perPageLimit = get(orders, 'pagination.limit')
  ) => {
    setLoading(true);
    const pageObj = generatePaginationObj(page, perPageLimit);

    dispatch(
      campaignManagement('list.orders_by_campaign', {
        id: parseInt(id),
        company_id: parseInt(companyId),
        ...pageObj,
        filters: filterObj,
      })
    )
      .then((result) => {
        setLoading(false);
        setOrders({
          data: get(result, 'data'),
          sorters: sorterObj,
          filters: filterObj,
          pagination: {
            ...pageObj,
            total: get(result, 'total'),
            current: page,
          },
          filterChips: filterChipsObj,
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCampaignDetails();
    fetchLocationsList();
    fetchCampaignOrders();
  }, []);

  const { locations, retailers, campaignTouchPoints } = getTouchPointMeta(
    get(campaignDetails, 'CampaignTouchpoints')
  );

  const onFinish = (values) => {
    const payload = {
      id: parseInt(id),
      company_id: parseInt(companyId),
      name: get(values, 'name'),
      valid_from: get(values, 'time_range.0').format('YYYY-MM-DD'),
      valid_to: get(values, 'time_range.1').format('YYYY-MM-DD'),
      is_active: get(values, 'is_active'),
    };
    setLoading(true);
    dispatch(campaignManagement('update.campaign', payload))
      .then(() => {
        message.success(UPDATE_CAMPAIGN_SUCCESS);
        fetchCampaignDetails();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onEdit = () => {
    form.setFieldsValue({
      name: get(campaignDetails, 'name'),
      is_active: get(campaignDetails, 'is_active'),
      time_range: [
        moment(get(campaignDetails, 'valid_from')),
        moment(get(campaignDetails, 'valid_to')),
      ],
    });
    setEditMode(true);
  };

  const onDownloadCampaignCSV = () => {
    setLoading(true);
    dispatch(
      campaignManagement('export.orders_by_campaign', {
        company_id: parseInt(companyId),
        id: parseInt(id),
        filters: get(orders, 'filters'),
      })
    )
      .then((result) => {
        setLoading(false);
        message.success(DOWNLOAD_CAMPAIGN_CSV_SUCCESS);
        window.open(get(result, 'data.file_path'));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const genderMap = {
    M: 'Male',
    F: 'Female',
    A: 'Both',
  };

  return (
    <Spin spinning={loading}>
      <Form
        name="basic"
        layout="vertical"
        form={form}
        initialValues={{}}
        onFinish={onFinish}
        autoComplete="off"
        requiredMark
      >
        <div>
          <ContentPageHeader
            border
            title={[
              <Link href={CAMPAIGN} className="content-page-header-heading">
                Campaign List
              </Link>,
              <RightOutlined className="content-page-header-heading" />,
              <Text className="content-page-header-heading">
                Campaign Details
              </Text>,
            ]}
            extra={[
              !editMode && (
                <Button
                  className="default-primary-btn"
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={onEdit}
                >
                  Edit
                </Button>
              ),
              editMode && (
                <Button
                  className="default-primary-btn"
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                >
                  Update
                </Button>
              ),
            ]}
          />
        </div>

        <div className="product-final-confirmation-container mt-30 ">
          <div className="product-description-container pd-30">
            <Descriptions column={4} size="default">
              <Descriptions.Item label="Offering Name">
                {get(campaignDetails, 'Offering.name')}
              </Descriptions.Item>
              <Descriptions.Item label="Channel ">
                {get(campaignDetails, 'SalesChannel.name')}
              </Descriptions.Item>
              <Descriptions.Item label="Brand">
                {get(campaignDetails, 'Company.name')}
              </Descriptions.Item>
              <Descriptions.Item label="Campaign Name">
                {!editMode && get(campaignDetails, 'name')}
                {editMode && (
                  <Form.Item
                    name="name"
                    className="form-item"
                    rules={[
                      {
                        required: true,
                        message: 'Please input name',
                      },
                    ]}
                  >
                    <Input size="default" />
                  </Form.Item>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Campaign Type">
                {isSample ? 'Sample' : 'Retail'}
              </Descriptions.Item>
              <Descriptions.Item label="Offering Code">
                {get(campaignDetails, 'Offering.code')}
              </Descriptions.Item>
              {isSample && (
                <Descriptions.Item label="Age Group">
                  {get(campaignDetails, 'age_group_from')} -
                  {get(campaignDetails, 'age_group_to')} Years
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Date Created">
                {moment(get(campaignDetails, 'created_at')).format(
                  'DD/MM/YYYY (HH:mm A)'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Location">
                <Space>
                  {locations.length}
                  <Popover
                    placement="bottomRight"
                    title="Locations"
                    content={<Content array={locations} />}
                    trigger="click"
                  >
                    <InfoCircleOutlined />
                  </Popover>
                </Space>
              </Descriptions.Item>
              {isSample && (
                <Descriptions.Item label="Item/Day">
                  {get(campaignDetails, 'item_per_user')}
                </Descriptions.Item>
              )}
              {isSample && (
                <Descriptions.Item label="Gender">
                  {get(genderMap, get(campaignDetails, 'gender'))}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Time Period" span={2}>
                {!editMode &&
                  `${moment(get(campaignDetails, 'valid_from')).format(
                    'DD/MM/YYYY'
                  )} - ${moment(get(campaignDetails, 'valid_to')).format(
                    'DD/MM/YYYY'
                  )}`}

                {editMode && (
                  <Form.Item
                    name="time_range"
                    className="form-item"
                    style={{ marginTop: -5 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please select time period!',
                      },
                    ]}
                  >
                    <RangePicker
                      style={{ marginTop: -10 }}
                      size="small"
                      format="DD/MM/YYYY"
                    />
                  </Form.Item>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Retailer">
                <Space>
                  {retailers.length}
                  <Popover
                    placement="bottomRight"
                    title="Retailers"
                    content={<Content array={retailers} />}
                    trigger="click"
                  >
                    <InfoCircleOutlined />
                  </Popover>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Touchpoints">
                <Space>
                  {campaignTouchPoints.length}
                  <Popover
                    placement="bottomRight"
                    title="Touchpoints"
                    content={<Content array={campaignTouchPoints} />}
                    trigger="click"
                  >
                    <InfoCircleOutlined />
                  </Popover>
                </Space>
              </Descriptions.Item>
              {!editMode && (
                <Descriptions.Item label="Status">
                  {get(campaignDetails, 'is_active') ? (
                    <Text type="success">Active</Text>
                  ) : (
                    <Text type="danger"> Inactive</Text>
                  )}
                </Descriptions.Item>
              )}
              {editMode && (
                <Descriptions.Item label="Status">
                  <Form.Item
                    name="is_active"
                    className="form-item"
                    style={{ marginTop: -5 }}
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        </div>
      </Form>
      <div className="product-final-confirmation-container mt-30 ">
        <div className="d-flex" style={{ justifyContent: 'space-between' }}>
          <p className="normal-heading ">Details Collected </p>
          <div className="d-flex" style={{ gap: '32px' }}>
            <Descriptions bordered size="small">
              <Descriptions.Item
                label={`Total Orders :  ${get(orders, 'pagination.total')}`}
              />
            </Descriptions>
            <Button type="primary" onClick={() => onDownloadCampaignCSV()}>
              <DownloadOutlined /> Export
            </Button>
          </div>
        </div>

        <div className="d-flex filter" style={{ alignItems: 'flex-start' }}>
          <TableFilters
            filtersList={campaignDetailsTableFiltersList(
              locationsList,
              touchpointsList
            )}
            tableData={orders}
            fetchTableData={fetchCampaignOrders}
            tempFilter={tempFilter}
            setTempFilter={setTempFilter}
            fetchDependentOptions={fetchTouchpointsList}
            roleCode={get(role, 'role_code')}
          />
        </div>

        <div className="d-flex" style={{ justifyContent: 'space-between' }}>
          <ShowPaginationLimits
            tableData={orders}
            offset={offset}
            limit={limit}
            fetchTableData={fetchCampaignOrders}
            filterChips={get(tempFilter, 'tempFilterChips')}
          />
        </div>

        <div className="product-description-container pd-30 mt-20">
          <Table
            rowKey="id"
            className="product-table"
            dataSource={get(orders, 'data')}
            columns={
              isSample ? sampleCampaigndetails() : retailCampaigndetails()
            }
            pagination={{
              showSizeChanger: false,
              position: ['bottomCenter'],
              hideOnSinglePage: true,
              pageSize: get(orders, 'pagination.limit'),
              total: get(orders, 'pagination.total'),
              current: get(orders, 'pagination.current'),
            }}
            onChange={(paginationObj, filterObj, sorterObj) => {
              fetchCampaignOrders(
                get(paginationObj, 'current'),
                get(orders, 'filters'),
                get(sorterObj, 'order')
                  ? {
                      sortBy: get(sorterObj, 'field'),
                      sortDir:
                        get(sorterObj, 'order') === 'ascend' ? 'ASC' : 'DESC',
                    }
                  : {},
                get(tempFilter, 'tempFilterChips')
              );
            }}
          />
        </div>
      </div>
    </Spin>
  );
};

export default CampaignDetailScreen;
